// UltraFlex Admin Portal - Sites Management with Full CRUD
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Building2, MapPin, Phone, Mail, Clock, Users, 
  Plus, Edit, Trash2, Save, X, Eye, Settings,
  CheckCircle, XCircle, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSites = () => {
  const { token } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [siteStats, setSiteStats] = useState({});
  const [expandedSite, setExpandedSite] = useState(null);

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sites`);
      const data = await res.json();
      const sitesData = data.items || [];
      setSites(sitesData);

      // Fetch stats for each site
      const statsPromises = sitesData.map(async (site) => {
        try {
          const statsRes = await fetch(`${API_URL}/api/sites/${site.id}/stats`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (statsRes.ok) {
            const statsData = await statsRes.json();
            return { siteId: site.id, stats: statsData };
          }
          return { siteId: site.id, stats: null };
        } catch {
          return { siteId: site.id, stats: null };
        }
      });

      const allStats = await Promise.all(statsPromises);
      const statsMap = {};
      allStats.forEach(({ siteId, stats }) => {
        statsMap[siteId] = stats;
      });
      setSiteStats(statsMap);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSite = async (siteId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/sites/${siteId}`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        fetchSites();
        setShowEditModal(false);
        setSelectedSite(null);
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      alert('Failed to update site');
    }
  };

  const handleAddSite = async (siteData) => {
    try {
      const res = await fetch(`${API_URL}/api/sites`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(siteData)
      });
      if (res.ok) {
        fetchSites();
        setShowAddModal(false);
      } else {
        throw new Error('Failed to create');
      }
    } catch (error) {
      alert('Failed to create site');
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Are you sure you want to delete this site? This action cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/sites/${siteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchSites();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      alert('Failed to delete site');
    }
  };

  return (
    <div className="min-h-screen bg-uf-black" data-testid="admin-sites-page">
      {/* Header */}
      <div className="bg-uf-surface border-b border-uf-border px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-uf-text">Site Management</h1>
            <p className="text-uf-text-muted mt-1">Manage all UltraFlex gym locations, hours, and configuration</p>
          </div>
          <button 
            onClick={() => setShowAddModal(true)}
            className="flex items-center px-4 py-2 bg-uf-red text-white rounded-lg hover:bg-uf-red-hover"
            data-testid="add-site-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Site
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="px-8 py-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Sites" 
          value={sites.length}
          icon={Building2}
          color="blue"
        />
        <StatCard 
          title="Active Sites" 
          value={sites.filter(s => s.is_active).length}
          icon={CheckCircle}
          color="green"
        />
        <StatCard 
          title="Total Members" 
          value={Object.values(siteStats).reduce((sum, s) => sum + (s?.active_members || 0), 0).toLocaleString()}
          icon={Users}
          color="purple"
        />
        <StatCard 
          title="Inactive Sites" 
          value={sites.filter(s => !s.is_active).length}
          icon={XCircle}
          color="orange"
        />
      </div>

      {/* Sites List */}
      <div className="px-8 pb-8">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {sites.map((site) => (
              <SiteRow 
                key={site.id}
                site={site}
                stats={siteStats[site.id]}
                isExpanded={expandedSite === site.id}
                onToggle={() => setExpandedSite(expandedSite === site.id ? null : site.id)}
                onEdit={() => { setSelectedSite(site); setShowEditModal(true); }}
                onDelete={() => handleDeleteSite(site.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Site Modal */}
      {showAddModal && (
        <SiteFormModal 
          onClose={() => setShowAddModal(false)}
          onSave={handleAddSite}
          isNew
        />
      )}

      {/* Edit Site Modal */}
      {showEditModal && selectedSite && (
        <SiteFormModal 
          site={selectedSite}
          onClose={() => { setShowEditModal(false); setSelectedSite(null); }}
          onSave={(updates) => handleSaveSite(selectedSite.id, updates)}
        />
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-uf-surface rounded-lg p-5 border border-uf-border">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-uf-text-muted text-sm">{title}</p>
        <p className="text-2xl font-bold text-uf-text mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-lg bg-${color}-500/10`}>
        <Icon className={`w-6 h-6 text-${color}-500`} />
      </div>
    </div>
  </div>
);

const SiteRow = ({ site, stats, isExpanded, onToggle, onEdit, onDelete }) => (
  <div className="bg-uf-surface rounded-lg border border-uf-border overflow-hidden">
    {/* Main Row */}
    <div 
      className="p-4 flex items-center justify-between cursor-pointer hover:bg-uf-surface-hi"
      onClick={onToggle}
    >
      <div className="flex items-center gap-4">
        <div className={`w-3 h-3 rounded-full ${site.is_active ? 'bg-green-500' : 'bg-gray-500'}`} />
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-uf-red bg-uf-red/10 px-2 py-0.5 rounded">
              {site.code}
            </span>
            <h3 className="font-semibold text-uf-text">{site.name}</h3>
          </div>
          <p className="text-uf-text-muted text-sm">{site.city}, {site.postcode}</p>
        </div>
      </div>

      <div className="flex items-center gap-6">
        {stats && (
          <div className="hidden md:flex items-center gap-6 text-sm">
            <div className="text-center">
              <p className="text-uf-text font-semibold">{stats.active_members || 0}</p>
              <p className="text-uf-text-muted text-xs">Members</p>
            </div>
            <div className="text-center">
              <p className="text-uf-text font-semibold">{stats.today_checkins || 0}</p>
              <p className="text-uf-text-muted text-xs">Today</p>
            </div>
          </div>
        )}
        
        <div className="flex items-center gap-2">
          <button 
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="p-2 text-uf-text-muted hover:text-uf-red hover:bg-uf-red/10 rounded"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-2 text-uf-text-muted hover:text-red-500 hover:bg-red-500/10 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-uf-text-muted" /> : <ChevronDown className="w-5 h-5 text-uf-text-muted" />}
        </div>
      </div>
    </div>

    {/* Expanded Details */}
    {isExpanded && (
      <div className="border-t border-uf-border p-6 bg-uf-black/50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Contact Info */}
          <div>
            <h4 className="text-sm font-semibold text-uf-text-muted uppercase tracking-wider mb-3">Contact</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center text-uf-text">
                <MapPin className="w-4 h-4 mr-2 text-uf-red" />
                {site.address}, {site.city}, {site.postcode}
              </div>
              {site.phone && (
                <div className="flex items-center text-uf-text">
                  <Phone className="w-4 h-4 mr-2 text-uf-red" />
                  {site.phone}
                </div>
              )}
              {site.email && (
                <div className="flex items-center text-uf-text">
                  <Mail className="w-4 h-4 mr-2 text-uf-red" />
                  {site.email}
                </div>
              )}
            </div>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="text-sm font-semibold text-uf-text-muted uppercase tracking-wider mb-3">Opening Hours</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-uf-text-muted">Weekdays</span>
                <span className="text-uf-text">{site.opening_hours?.weekdays || 'Not set'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-uf-text-muted">Saturday</span>
                <span className="text-uf-text">{site.opening_hours?.saturday || 'Not set'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-uf-text-muted">Sunday</span>
                <span className="text-uf-text">{site.opening_hours?.sunday || 'Not set'}</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div>
            <h4 className="text-sm font-semibold text-uf-text-muted uppercase tracking-wider mb-3">Statistics</h4>
            {stats ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-uf-surface rounded p-3 text-center">
                  <p className="text-xl font-bold text-uf-text">{stats.active_members || 0}</p>
                  <p className="text-xs text-uf-text-muted">Active Members</p>
                </div>
                <div className="bg-uf-surface rounded p-3 text-center">
                  <p className="text-xl font-bold text-green-400">+{stats.new_members || 0}</p>
                  <p className="text-xs text-uf-text-muted">New (30d)</p>
                </div>
                <div className="bg-uf-surface rounded p-3 text-center">
                  <p className="text-xl font-bold text-uf-text">{stats.today_checkins || 0}</p>
                  <p className="text-xs text-uf-text-muted">Today</p>
                </div>
                <div className="bg-uf-surface rounded p-3 text-center">
                  <p className="text-xl font-bold text-orange-400">{stats.cancellations || 0}</p>
                  <p className="text-xs text-uf-text-muted">Cancellations</p>
                </div>
              </div>
            ) : (
              <p className="text-uf-text-muted text-sm">No stats available</p>
            )}
          </div>
        </div>

        {/* Features */}
        {site.features && site.features.length > 0 && (
          <div className="mt-6 pt-6 border-t border-uf-border">
            <h4 className="text-sm font-semibold text-uf-text-muted uppercase tracking-wider mb-3">Features & Amenities</h4>
            <div className="flex flex-wrap gap-2">
              {site.features.map((feature, i) => (
                <span key={i} className="px-3 py-1 bg-uf-surface text-uf-text text-sm rounded border border-uf-border">
                  {feature}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

const SiteFormModal = ({ site, onClose, onSave, isNew = false }) => {
  const [formData, setFormData] = useState({
    code: site?.code || '',
    name: site?.name || '',
    address: site?.address || '',
    city: site?.city || '',
    postcode: site?.postcode || '',
    phone: site?.phone || '',
    email: site?.email || '',
    is_active: site?.is_active ?? true,
    capacity: site?.capacity || 200,
    opening_hours: site?.opening_hours || { 
      weekdays: '06:00 - 22:00', 
      saturday: '08:00 - 20:00', 
      sunday: '09:00 - 18:00' 
    },
    features: site?.features || []
  });
  const [newFeature, setNewFeature] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData, 
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-uf-surface rounded-lg border border-uf-border w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-uf-border flex items-center justify-between">
          <h2 className="text-xl font-bold text-uf-text">
            {isNew ? 'Add New Site' : `Edit Site: ${site?.code}`}
          </h2>
          <button onClick={onClose} className="text-uf-text-muted hover:text-uf-text">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[70vh]">
          {/* Basic Info */}
          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-semibold text-uf-text-muted uppercase tracking-wider">Basic Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-uf-text-muted mb-1">Site Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="e.g., UF-LON-01"
                  required
                  className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-uf-text-muted mb-1">Site Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., London Shoreditch"
                  required
                  className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Address *</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                required
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-uf-text-muted mb-1">City *</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  required
                  className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-uf-text-muted mb-1">Postcode *</label>
                <input
                  type="text"
                  value={formData.postcode}
                  onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                  required
                  className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-semibold text-uf-text-muted uppercase tracking-wider">Contact Information</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-uf-text-muted mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-uf-text-muted mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Opening Hours */}
          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-semibold text-uf-text-muted uppercase tracking-wider flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              Opening Hours
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <label className="w-24 text-sm text-uf-text-muted">Weekdays</label>
                <input
                  type="text"
                  value={formData.opening_hours.weekdays}
                  onChange={(e) => setFormData({
                    ...formData, 
                    opening_hours: {...formData.opening_hours, weekdays: e.target.value}
                  })}
                  placeholder="e.g., 06:00 - 22:00"
                  className="flex-1 px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="w-24 text-sm text-uf-text-muted">Saturday</label>
                <input
                  type="text"
                  value={formData.opening_hours.saturday}
                  onChange={(e) => setFormData({
                    ...formData, 
                    opening_hours: {...formData.opening_hours, saturday: e.target.value}
                  })}
                  placeholder="e.g., 08:00 - 20:00"
                  className="flex-1 px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="w-24 text-sm text-uf-text-muted">Sunday</label>
                <input
                  type="text"
                  value={formData.opening_hours.sunday}
                  onChange={(e) => setFormData({
                    ...formData, 
                    opening_hours: {...formData.opening_hours, sunday: e.target.value}
                  })}
                  placeholder="e.g., 09:00 - 18:00"
                  className="flex-1 px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Capacity */}
          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-semibold text-uf-text-muted uppercase tracking-wider">Configuration</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-uf-text-muted mb-1">Capacity (people)</label>
                <input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value) || 0})}
                  min="1"
                  className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-5 h-5 rounded border-uf-border text-uf-red focus:ring-uf-red"
                  />
                  <span className="text-uf-text">Site is active</span>
                </label>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-semibold text-uf-text-muted uppercase tracking-wider">Features & Amenities</h3>
            
            <div className="flex gap-2">
              <input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                placeholder="Add a feature (e.g., Sauna, Free Parking)"
                className="flex-1 px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              />
              <button
                type="button"
                onClick={addFeature}
                className="px-4 py-2 bg-uf-surface-hi border border-uf-border rounded-lg text-uf-text hover:bg-uf-red hover:border-uf-red"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {formData.features.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.features.map((feature, i) => (
                  <span 
                    key={i} 
                    className="inline-flex items-center px-3 py-1 bg-uf-black text-uf-text text-sm rounded border border-uf-border"
                  >
                    {feature}
                    <button 
                      type="button"
                      onClick={() => removeFeature(i)}
                      className="ml-2 text-uf-text-muted hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </form>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-uf-border flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-uf-border text-uf-text rounded-lg hover:bg-uf-surface-hi"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-uf-red text-white rounded-lg hover:bg-uf-red-hover disabled:opacity-50 flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : isNew ? 'Create Site' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSites;
